import { ChangeEvent, FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Translate } from "@/app/application/Translate";
import {
	ManualMeasurementPointSummary,
	MeasurementsModal,
} from "@/features/measurements";
import { useUpdateStepStatus } from "@/features/procedures/api/checklist.mutations";
import { useFetchChecklistStepStatuses } from "@/features/procedures/api/checklist.queries";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";
import { UpdateStepStatusRequest } from "@/features/procedures/model/types/checklist";
import { ProcedureIconButton } from "@/features/procedures/ui/components/common/ProcedureIconButton";
import { ErrorToast } from "@/shared/notifications/AlertToast";
import SaveIcon from "@mui/icons-material/Save";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import {
	Box,
	ButtonGroup,
	Chip,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);

type Props = {
	step: ProcedureNodeStep;
	activeSchemeId: number | undefined;
	isDisabled?: boolean;
};

export const StepManualMeasurements: FC<Props> = ({
	step,
	activeSchemeId,
	isDisabled,
}) => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	const [saveStateSuccess, setSaveStateSuccess] = useState<boolean>(true);
	const [isSaving, setIsSaving] = useState(false);

	// keep what the user typed
	const [inputValue, setInputValue] = useState<string>("");

	// keep the parsed number that will be sent to the API
	const [manualRegistration, setManualRegistration] = useState<
		number | undefined
	>(undefined);

	const [measurementModalOpen, toggleMeasurementModalOpen] =
		useState<boolean>(false);

	const { data: stepStatuses } = useFetchChecklistStepStatuses(
		activeSchemeId,
		procedureId,
	);
	const currentStepStatus = Array.isArray(stepStatuses)
		? stepStatuses.find((x) => x.Id === step.Id)
		: undefined;

	// Use enriched step value when statuses haven't loaded yet (from useChecklistLiveData)
	const measurementValue =
		currentStepStatus?.ManualMeasurementValue ??
		(step as any)?.ManualMeasurementValue;

	const updateStepStatus = useUpdateStepStatus();

	// initialize from step status or enriched step (so saved values show immediately)
	useEffect(() => {
		if (measurementValue != null) {
			const val = +measurementValue;
			setManualRegistration(val);
			setInputValue(String(measurementValue));
			setSaveStateSuccess(true);
		} else {
			setSaveStateSuccess(false);
			setInputValue("");
		}
	}, [measurementValue]);

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setInputValue(value); // always reflect what user types

		if (!value) {
			setManualRegistration(undefined);
			return;
		}

		// allow numbers with , or .
		const regex = /^-?\d*(?:[.,]\d*)?$/;
		if (regex.test(value)) {
			// only parse when it's not just "-" or ends with "," or "."
			const endsWithSeparator = value.endsWith(",") || value.endsWith(".");
			const isOnlyMinus = value === "-";

			if (!endsWithSeparator && !isOnlyMinus) {
				const normalized = value.replace(",", ".");
				const num = Number(normalized);
				if (!Number.isNaN(num)) {
					setManualRegistration(num);
				} else {
					setManualRegistration(undefined);
				}
			}
		} else {
			// invalid char – show error but don't change the parsed number
			ErrorToast(
				<Translate String="Procedure.Checklist.ManualReading.ValueField.Error">
					Please enter a valid number.
				</Translate>,
			);
		}
	};

	const Save = async () => {
		if (isSaving) return;
		setIsSaving(true);
		try {
			const request: UpdateStepStatusRequest = {
				ArchiveType: "Measurement",
				MeasurementId: step.ManualReading?.Id,
				ProcedureId: procedureId,
				SchemeId: activeSchemeId,
				Status: 1,
				StepId: step.Id,
				Value: manualRegistration,
			};
			await updateStepStatus(request);

			setSaveStateSuccess(true);
		} catch (error: any) {
			setSaveStateSuccess(false);
			ErrorToast(error.message);
		} finally {
			setIsSaving(false);
		}
	};

	if (!step?.ManualReading) {
		return <></>;
	}

	const theme = useTheme();
	const isDark = theme.palette.mode === "dark";

	const chipBg = isDark
		? saveStateSuccess
			? "rgba(76, 175, 80, 0.25)"
			: "#2a2a2a"
		: saveStateSuccess
			? green[300]
			: grey[200];
	const chipTextColor = isDark ? "#ffffff" : grey[900];
	const inputBg = isDark
		? saveStateSuccess
			? "rgba(76, 175, 80, 0.15)"
			: "#1e1e1e"
		: saveStateSuccess
			? green[200]
			: "primary";
	const inputTextColor = isDark ? "#ffffff" : grey[800];
	const inputBorderColor = isDark ? "rgba(255, 255, 255, 0.23)" : grey[800];

	const initialManualReading: ManualMeasurementPointSummary = {
		Id: step.ManualReading.Id,
		Name: step.ManualReading.Name,
	};

	return (
		<Box sx={{ display: "flex" }}>
			<Chip
				sx={{
					height: "120px",
					backgroundColor: chipBg,
					color: chipTextColor,
					boxShadow: 2,
					"& .MuiChip-label": {
						color: "inherit",
					},
				}}
				label={
					<Box display="flex" flexDirection="column" gap={0.25} p={2}>
						<Typography color="inherit" sx={{ mb: 1.5 }}>
							{step.ManualReading?.Name}
						</Typography>
						<TextField
							sx={{
								alignSelf: "center",
								width: "100%",
								borderWidth: "0px",
								"& .MuiOutlinedInput-root": {
									backgroundColor: inputBg,
									color: inputTextColor,
									"& fieldset": {
										borderColor: inputBorderColor,
									},
									"&.Mui-focused fieldset": {
										borderColor: isDark
											? "rgba(255, 255, 255, 0.5)"
											: grey[800],
										borderWidth: "1px",
									},
								},
								"& .MuiInputLabel-root": {
									color: isDark ? "rgba(255, 255, 255, 0.7)" : undefined,
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: isDark ? "rgba(255, 255, 255, 0.9)" : undefined,
								},
							}}
							size="small"
							focused
							disabled={isDisabled}
							name={`${step.ManualReading.Id}`}
							value={inputValue}
							inputProps={{
								inputMode: "decimal",
								pattern: "[0-9]*[.,]?[0-9]*",
							}}
							onKeyDown={(e) => {
								// allow control keys + , .
								if (
									[
										"Backspace",
										"Delete",
										"Tab",
										"Escape",
										"Enter",
										"ArrowLeft",
										"ArrowRight",
										".",
										",",
										"-",
									].includes(e.key)
								) {
									return;
								}
								if (!/^[0-9]$/.test(e.key)) {
									e.preventDefault();
								}
							}}
							label={
								<Translate String="Procedure.Checklist.ManualReading.ValueField">
									Value
								</Translate>
							}
							onChange={onChange}
						/>
						<Typography variant="caption" color="inherit" sx={{ gap: 0 }}>
							<Translate String="Procedure.Checklist.ManualReading.ValueRange">
								Range: {step.ManualReading.Min} - {step.ManualReading.Max}
							</Translate>
						</Typography>
					</Box>
				}
			/>
			<ButtonGroup
				orientation="vertical"
				sx={{
					ml: 1,
				}}
			>
				<Tooltip
					title={
						<Translate String="Checklist.Measurement.Page.Button.ToolTip">
							Measurement page
						</Translate>
					}
					placement="top"
				>
					<ProcedureIconButton
						tone="info"
						onClick={() => toggleMeasurementModalOpen(true)}
					>
						<StackedLineChartIcon />
					</ProcedureIconButton>
				</Tooltip>
				<Tooltip
					title={
						<Translate String="Checklist.Measurement.Save.Button.ToolTip">
							Save
						</Translate>
					}
				>
					<ProcedureIconButton
						tone="success"
						onClick={() => {
							void Save();
						}}
						disabled={saveStateSuccess || isSaving}
						loading={isSaving}
					>
						<SaveIcon />
					</ProcedureIconButton>
				</Tooltip>
			</ButtonGroup>
			<MeasurementsModal
				open={measurementModalOpen}
				onClose={() => toggleMeasurementModalOpen(false)}
				title={step.ManualReading?.Name ?? ""}
				initialSelectedMeasurement={initialManualReading}
			/>
		</Box>
	);
};
